/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2226-106325&m=dev
 *
 * Reusable timeline for order status (ordered, shipped, delivered).
 */

import { motion } from 'framer-motion'
import { cn } from '../../../utils/cn'

import { type LabelKey, useLabel } from '../../../i18n'
import type { TimelineLabels, TimelineStep, TimelineVariant } from '../types'

const STEPS_BY_VARIANT: Record<TimelineVariant, TimelineStep[]> = {
  order: ['ordered', 'shipped', 'delivered'],
  alteration: ['being-altered', 'ready'],
  // Inbound leg of a return/exchange, then the exchange's outbound leg. The
  // inbound leg has no in-transit status in the feed, so it is two steps: a
  // Shipped checkpoint could never light up (RP-6203).
  return: ['registered', 'received'],
  exchange: ['exchanged', 'shipped', 'delivered'],
}

/** Fallbacks for hosts that do not pass `labels`. */
const STEP_LABEL_KEYS: Record<TimelineStep, LabelKey> = {
  ordered: 'orderList.timeline.ordered',
  shipped: 'orderList.timeline.shipped',
  delivered: 'orderList.timeline.delivered',
  'being-altered': 'orderList.timeline.beingAltered',
  ready: 'orderList.timeline.ready',
  registered: 'orderList.timeline.registered',
  received: 'orderList.timeline.received',
  exchanged: 'orderList.timeline.exchanged',
}

interface OrderStatusTimelineProps {
  currentStep: TimelineStep
  variant?: TimelineVariant
  /** Translated step captions; missing entries fall back to English. */
  labels?: TimelineLabels
}

const pulseVariants = {
  initial: { scale: 1 },
  pulse: {
    scale: [1, 1.2, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatDelay: 1.5,
    },
  },
}

export default function OrderStatusTimeline({
  currentStep,
  variant = 'order',
  labels,
}: OrderStatusTimelineProps) {
  const t = useLabel()
  const steps = STEPS_BY_VARIANT[variant]
  const currentIndex = steps.indexOf(currentStep)

  return (
    <div className="flex w-full flex-col gap-8">
      {/* Checkpoints and lines row */}
      <div className="flex w-full items-center">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex
          const isActive = index === currentIndex
          const isLast = index === steps.length - 1
          // The line leaving the current checkpoint is filled too — it reads as
          // progress towards the next step.
          const isLineFilled = index <= currentIndex
          const isLineAnimated = index === currentIndex

          return (
            <div key={step} className="flex min-w-0 flex-1 items-center">
              {/* Checkpoint */}
              {isActive ? (
                <motion.div
                  className="size-8 shrink-0 rounded-sm bg-surface-inverse-alt"
                  variants={pulseVariants}
                  initial="initial"
                  animate="pulse"
                />
              ) : (
                <div
                  className={cn(
                    'size-8 shrink-0 rounded-sm',
                    isCompleted
                      ? 'bg-surface-inverse-alt'
                      : 'border border-border bg-background',
                  )}
                />
              )}

              {/* Connector line — every step has one, including the last,
                  whose line runs out to the edge of the row. */}
              <div className={cn('ml-4 flex flex-1', !isLast && 'mr-4')}>
                {isLineAnimated ? (
                  <motion.div
                    className="h-1 w-full bg-surface-inverse-alt"
                    data-testid="timeline-line"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{
                      type: 'spring' as const,
                      stiffness: 300,
                      damping: 24,
                    }}
                    style={{ transformOrigin: 'left' }}
                  />
                ) : (
                  <div
                    className={cn(
                      'h-1 w-full',
                      isLineFilled
                        ? 'bg-surface-inverse-alt'
                        : 'bg-surface-alt-2',
                    )}
                    data-testid="timeline-line"
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Labels row */}
      <div className="flex w-full">
        {steps.map((step, index) => {
          const isReached = index <= currentIndex

          return (
            <div key={step} className="flex min-w-0 flex-1">
              <span
                className={cn(
                  'text-xs leading-xs font-medium',
                  isReached ? 'text-text' : 'text-text-disabled',
                )}
              >
                {labels?.[step] ?? t(STEP_LABEL_KEYS[step])}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
